// React
import { useMemo } from 'react';

// Custom components
import UserAvatar from 'components/Avatar';
import TeamAvatar from 'components/Avatar/TeamAvatar/TeamAvatar.cmp';
import { CategoryChip } from 'pages/Schedules/common/components';

// Icons
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import RepeatIconMultiColor from 'components/Icons/repeatIconMultiColor';
import LocationIcon from 'components/Icons/locationIcon';
import ChecklistIcon from 'components/Icons/checklistIcon';
import CalendarIconRounded from 'components/Icons/calendarIconRounded';
import AssetsIcon from 'components/Icons/assetsIcon';
import MissedIcon from 'components/Icons/missedIcon';

// Styled
import {
  InfoChip,
  ScheduleItemCnt,
  LeftBorder,
  CreatedByWrapper,
} from './styled';

// Hooks
import { useSchedule } from 'pages/Schedules/common/useSchedule';

interface MyScheduleItemPropTypes {
  task: any;
  hideLeftBar?: boolean;
  onClick?: (task: any) => void;
}

function ScheduleGridItem({
  task,
  hideLeftBar = false,
  onClick,
}: MyScheduleItemPropTypes) {
  const {
    taskTemplate,
    taskAssignee,
    isTaskOverdue,
    taskInterval,
    isTaskMissed,
    getFormattedDates,
  } = useSchedule(task);

  const formattedDates = useMemo(() => getFormattedDates(), [task]);

  return (
    <ScheduleItemCnt
      clickable={onClick ? 'true' : ''}
      onClick={() => onClick?.(task)}
    >
      {!hideLeftBar && <LeftBorder status={task.taskStatus} />}
      <div className={'header'}>
        <div className={'column'}>
          <h3 className={'title'}>{task.title}</h3>
          {/* Has no due date and is not completed yet */}
          {!task.Finisher && !task.dueDate && (
            <CreatedByWrapper>
              <span className={'createdBy'}>Created by:</span>
              <div className={'user'}>
                <UserAvatar width={16} height={16} userId={task.Creator?.id} />
                <span className={'createdBy'}>{task.Creator?.fullName}</span>
              </div>
            </CreatedByWrapper>
          )}
          {/* Has not been completed and not overdue */}
          {!task.Finisher && task.dueDate && !isTaskOverdue && (
            <div className={'dueOrcompleted'}>
              <CalendarIconRounded />
              Due by ({formattedDates.dueDate})
            </div>
          )}
          {/* Has been completed */}
          {task.Finisher && (
            <div className={'dueOrcompleted'}>
              <CalendarIconRounded />
              Completed on {formattedDates.completedOn}
            </div>
          )}
          {/* Is not finished but is overdue */}
          {isTaskOverdue && !isTaskMissed && (
            <div className={'overdue'}>
              <AccessTimeRoundedIcon sx={{ fontSize: 16 }} />
              Overdue ({formattedDates.dueDate})
            </div>
          )}
          {isTaskMissed && (
            <div className={'overdue'}>
              <MissedIcon sx={{ fontSize: 16 }} />
              Missed ({formattedDates.dueDate})
            </div>
          )}
        </div>
        {task.Finisher && (
          <InfoChip>
            <span className={'infoChipIcon'}>
              <CheckCircleIcon
                sx={{ fontSize: 14, color: 'rgba(4, 184, 108, 1)' }}
              />
            </span>
            <span className={'infoChipText'}>{task.Finisher.fullName}</span>
          </InfoChip>
        )}
      </div>
      {(taskTemplate ||
        task.requestThisTask ||
        task.NextDue ||
        task.Location ||
        task.ServiceId ||
        (taskAssignee && !task.Team) ||
        task.Team) && (
        <div className={'itemFooter'}>
          {taskTemplate && (
            <InfoChip>
              <span className={'infoChipIcon'}>
                <ChecklistIcon style={{ fontSize: 14, fill: 'none' }} />
              </span>
              <span className={'infoChipText'}>{taskTemplate.name}</span>
            </InfoChip>
          )}
          {taskInterval && (
            <InfoChip>
              <span className={'infoChipIcon'}>
                <RepeatIconMultiColor style={{ fontSize: 14, fill: 'none' }} />
              </span>
              <span className={'infoChipText'}>{taskInterval}</span>
            </InfoChip>
          )}
          {task.NextDue && task?.Asset?.name && (
            <InfoChip>
              <span className={'infoChipIcon'}>
                <AssetsIcon style={{ fontSize: 14, fill: 'none' }} />
              </span>
              <span className={'infoChipText'}>{task?.Asset?.name}</span>
            </InfoChip>
          )}
          {task.NextDue && (
            <InfoChip>
              <span className={'infoChipIcon'}>
                <CalendarIconRounded
                  style={{ fill: 'none', fontSize: 14 }}
                  sx={{
                    '& path': {
                      stroke: 'rgba(117, 117, 117, 1)',
                    },
                  }}
                />
              </span>
              <span className={'infoChipText'}>
                Next Due on {formattedDates.nextDue}
              </span>
            </InfoChip>
          )}
          {task.Location && task.LocationId && (
            <InfoChip>
              <span className={'infoChipIcon'}>
                <LocationIcon style={{ fontSize: 14, fill: 'none' }} />
              </span>
              <span className={'infoChipText'}>{task.Location.name}</span>
            </InfoChip>
          )}
          {task.ServiceId && (
            <CategoryChip
              sx={{ borderRadius: '100px', '& svg': { fontSize: 16 } }}
              border
              startIcon
              task={task}
            />
          )}
          {taskAssignee && !task.Team && (
            <InfoChip>
              <UserAvatar userId={taskAssignee.id} width={16} height={16} />
              <span className={'infoChipText'}>
                {taskAssignee.firstName} {taskAssignee.lastName}
              </span>
            </InfoChip>
          )}
          {task.Team && (
            <InfoChip>
              <TeamAvatar teamId={task.Team.id} width={16} height={16} />
              <span className={'infoChipText'}>{task.Team.name}</span>
            </InfoChip>
          )}
        </div>
      )}
    </ScheduleItemCnt>
  );
}

export default ScheduleGridItem;
